import { useState } from "react";
import usePersistentState from "@/hooks/usePersistentState";
import { ScheduleItem, STATUS_CONFIG, TYPE_CONFIG } from "../types";

interface Props {
  items: ScheduleItem[];
  onViewDetail: (item: ScheduleItem) => void;
  onEditTime: (item: ScheduleItem) => void;
  onCancel: (item: ScheduleItem) => void;
  onRestore: (item: ScheduleItem) => void;
}

const PAGE_SIZE = 8;

export default function ScheduleTable({ items, onViewDetail, onEditTime, onCancel, onRestore }: Props) {
  // 페이지 번호도 유지(새로고침·탭 복귀에도 보던 페이지 그대로)
  const [page, setPage] = usePersistentState<number>("schedule_page", 1);
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [mobileActionId, setMobileActionId] = useState<string | null>(null);

  const totalPages = Math.max(1, Math.ceil(items.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const paged = items.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const formatRecipientCount = (count: number) => {
    if (count >= 1000) return `${(count / 1000).toFixed(1)}k명`;
    return `${count}명`;
  };

  const getTimeRemaining = (scheduledAt: string) => {
    const now = new Date();
    const parts = scheduledAt.match(/(\d{4})\.(\d{2})\.(\d{2})\s(\d{2}):(\d{2})/);
    if (!parts) return null;
    const target = new Date(`${parts[1]}-${parts[2]}-${parts[3]}T${parts[4]}:${parts[5]}:00`);
    const diff = target.getTime() - now.getTime();
    if (diff <= 0) return null;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const mins = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    if (days > 0) return `${days}일 ${hours}시간 후`;
    if (hours > 0) return `${hours}시간 ${mins}분 후`;
    return `${mins}분 후`;
  };

  if (items.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 md:py-20 text-center">
        <div className="w-14 h-14 md:w-16 md:h-16 flex items-center justify-center rounded-2xl bg-white/[0.04] mb-3 md:mb-4">
          <i className="ri-calendar-schedule-line text-xl md:text-2xl text-gray-500"></i>
        </div>
        <p className="text-gray-400 text-sm font-medium">예약된 발송이 없습니다</p>
        <p className="text-gray-600 text-xs mt-1">문자 발송 시 예약 발송을 설정하면 여기에 표시됩니다</p>
      </div>
    );
  }

  return (
    <div>
      {/* Desktop table - hidden on mobile */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-white/[0.06]">
              <th className="text-left text-xs text-gray-500 font-medium px-4 py-3 whitespace-nowrap">유형</th>
              <th className="text-left text-xs text-gray-500 font-medium px-4 py-3">메시지</th>
              <th className="text-left text-xs text-gray-500 font-medium px-4 py-3 whitespace-nowrap">수신자</th>
              <th className="text-left text-xs text-gray-500 font-medium px-4 py-3 whitespace-nowrap">예약 시각</th>
              <th className="text-left text-xs text-gray-500 font-medium px-4 py-3 whitespace-nowrap">남은 시간</th>
              <th className="text-left text-xs text-gray-500 font-medium px-4 py-3 whitespace-nowrap">비용</th>
              <th className="text-left text-xs text-gray-500 font-medium px-4 py-3 whitespace-nowrap">상태</th>
              <th className="text-left text-xs text-gray-500 font-medium px-4 py-3 whitespace-nowrap">작업</th>
            </tr>
          </thead>
          <tbody>
            {paged.map((item) => {
              const statusCfg = STATUS_CONFIG[item.status];
              const typeCfg = TYPE_CONFIG[item.type];
              const remaining = item.status === "scheduled" ? getTimeRemaining(item.scheduledAt) : null;
              const isHovered = hoveredId === item.id;

              return (
                <tr
                  key={item.id}
                  className={`border-b border-white/[0.04] transition-colors cursor-pointer ${
                    isHovered ? "bg-white/[0.04]" : "hover:bg-white/[0.02]"
                  } ${item.status === "cancelled" ? "opacity-60" : ""}`}
                  onMouseEnter={() => setHoveredId(item.id)}
                  onMouseLeave={() => setHoveredId(null)}
                  onClick={() => onViewDetail(item)}
                >
                  <td className="px-4 py-3.5">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold ${typeCfg.className}`}>
                      {item.type}
                    </span>
                  </td>
                  <td className="px-4 py-3.5 max-w-xs">
                    {item.subject && (
                      <p className="text-white text-xs font-medium truncate mb-0.5">{item.subject}</p>
                    )}
                    <p className="text-gray-400 text-xs truncate">{item.message}</p>
                    {item.hasImage && (
                      <span className="inline-flex items-center gap-1 text-amber-400 text-xs mt-0.5">
                        <i className="ri-image-line text-xs"></i>
                        이미지 첨부
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3.5">
                    <div className="flex items-center gap-1.5">
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-group-line text-gray-400 text-xs"></i>
                      </div>
                      <span className="text-gray-300 text-xs font-medium">
                        {formatRecipientCount(item.recipients.length)}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3.5">
                    <div className="flex items-center gap-1.5">
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-calendar-line text-gray-500 text-xs"></i>
                      </div>
                      <span className="text-gray-300 text-xs whitespace-nowrap">{item.scheduledAt}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3.5">
                    {remaining ? (
                      <span className="text-violet-400 text-xs font-medium whitespace-nowrap">{remaining}</span>
                    ) : item.status === "cancelled" ? (
                      <span className="text-gray-600 text-xs">—</span>
                    ) : (
                      <span className="text-gray-600 text-xs">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3.5">
                    <span className="text-gray-300 text-xs whitespace-nowrap">
                      ₩{item.cost.toLocaleString()}
                    </span>
                  </td>
                  <td className="px-4 py-3.5">
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${statusCfg.className}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${statusCfg.dotClass} ${item.status === "sending" ? "animate-pulse" : ""}`}></span>
                      {statusCfg.label}
                    </span>
                  </td>
                  <td className="px-4 py-3.5" onClick={(e) => e.stopPropagation()}>
                    <div className={`flex items-center gap-1 transition-opacity ${isHovered ? "opacity-100" : "opacity-0"}`}>
                      {item.status === "scheduled" && (
                        <>
                          <button
                            onClick={() => onEditTime(item)}
                            className="w-7 h-7 flex items-center justify-center rounded-md bg-white/[0.06] text-gray-400 hover:text-white hover:bg-white/[0.1] cursor-pointer transition-colors"
                            title="시간 수정"
                          >
                            <i className="ri-time-line text-xs"></i>
                          </button>
                          <button
                            onClick={() => onCancel(item)}
                            className="w-7 h-7 flex items-center justify-center rounded-md bg-red-500/10 text-red-400 hover:bg-red-500/20 cursor-pointer transition-colors"
                            title="예약 취소"
                          >
                            <i className="ri-close-line text-xs"></i>
                          </button>
                        </>
                      )}
                      {item.status === "cancelled" && (
                        <button
                          onClick={() => onRestore(item)}
                          className="w-7 h-7 flex items-center justify-center rounded-md bg-violet-500/10 text-violet-400 hover:bg-violet-500/20 cursor-pointer transition-colors"
                          title="재예약"
                        >
                          <i className="ri-refresh-line text-xs"></i>
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Mobile card view */}
      <div className="md:hidden divide-y divide-white/[0.04]">
        {paged.map((item) => {
          const statusCfg = STATUS_CONFIG[item.status];
          const typeCfg = TYPE_CONFIG[item.type];
          const remaining = item.status === "scheduled" ? getTimeRemaining(item.scheduledAt) : null;
          const showActions = mobileActionId === item.id;

          return (
            <div
              key={item.id}
              className={`px-3 py-3 space-y-2.5 cursor-pointer transition-colors active:bg-white/[0.04] ${
                item.status === "cancelled" ? "opacity-60" : ""
              }`}
              onClick={() => onViewDetail(item)}
            >
              {/* Top row: type + status */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold ${typeCfg.className}`}>
                    {item.type}
                  </span>
                  {item.hasImage && (
                    <span className="inline-flex items-center gap-0.5 text-amber-400 text-[10px]">
                      <i className="ri-image-line text-[10px]"></i>
                      이미지
                    </span>
                  )}
                </div>
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${statusCfg.className}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${statusCfg.dotClass}`}></span>
                  {statusCfg.label}
                </span>
              </div>

              {/* Message preview */}
              <div>
                {item.subject && (
                  <p className="text-white text-xs font-medium truncate mb-0.5">{item.subject}</p>
                )}
                <p className="text-gray-400 text-xs line-clamp-2">{item.message}</p>
              </div>

              {/* Info grid */}
              <div className="grid grid-cols-2 gap-x-3 gap-y-1.5">
                <div className="flex items-center gap-1.5">
                  <div className="w-3.5 h-3.5 flex items-center justify-center">
                    <i className="ri-calendar-line text-gray-500 text-[10px]"></i>
                  </div>
                  <span className="text-gray-300 text-[10px]">{item.scheduledAt}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-3.5 h-3.5 flex items-center justify-center">
                    <i className="ri-group-line text-gray-500 text-[10px]"></i>
                  </div>
                  <span className="text-gray-300 text-[10px]">{formatRecipientCount(item.recipients.length)}</span>
                </div>
                {remaining && (
                  <div className="flex items-center gap-1.5">
                    <div className="w-3.5 h-3.5 flex items-center justify-center">
                      <i className="ri-timer-line text-violet-400 text-[10px]"></i>
                    </div>
                    <span className="text-violet-400 text-[10px] font-medium">{remaining}</span>
                  </div>
                )}
                <div className="flex items-center gap-1.5">
                  <div className="w-3.5 h-3.5 flex items-center justify-center">
                    <i className="ri-coin-line text-gray-500 text-[10px]"></i>
                  </div>
                  <span className="text-gray-300 text-[10px]">₩{item.cost.toLocaleString()}</span>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2 pt-0.5" onClick={(e) => e.stopPropagation()}>
                {item.status === "scheduled" && (
                  <>
                    <button
                      onClick={() => onEditTime(item)}
                      className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-white/[0.06] text-gray-400 hover:text-white text-[10px] cursor-pointer transition-colors whitespace-nowrap"
                    >
                      <i className="ri-time-line text-[10px]"></i>
                      시간 수정
                    </button>
                    <button
                      onClick={() => onCancel(item)}
                      className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-red-500/10 text-red-400 text-[10px] cursor-pointer transition-colors whitespace-nowrap"
                    >
                      <i className="ri-close-line text-[10px]"></i>
                      취소
                    </button>
                  </>
                )}
                {item.status === "cancelled" && (
                  <button
                    onClick={() => onRestore(item)}
                    className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-violet-500/10 text-violet-400 text-[10px] cursor-pointer transition-colors whitespace-nowrap"
                  >
                    <i className="ri-refresh-line text-[10px]"></i>
                    재예약
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between px-3 md:px-4 py-2.5 md:py-3 border-t border-white/[0.06]">
          <span className="text-gray-500 text-[10px] md:text-xs">
            총 {items.length}건 중 {(safePage - 1) * PAGE_SIZE + 1}–{Math.min(safePage * PAGE_SIZE, items.length)}건
          </span>
          <div className="flex items-center gap-0.5 md:gap-1">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={safePage === 1}
              className="w-6 h-6 md:w-7 md:h-7 flex items-center justify-center rounded-md text-gray-400 hover:text-white hover:bg-white/[0.06] disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer transition-colors"
            >
              <i className="ri-arrow-left-s-line text-xs md:text-sm"></i>
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
              <button
                key={p}
                onClick={() => setPage(p)}
                className={`w-6 h-6 md:w-7 md:h-7 flex items-center justify-center rounded-md text-[10px] md:text-xs cursor-pointer transition-colors ${
                  p === safePage
                    ? "bg-violet-600/40 text-violet-300 font-medium"
                    : "text-gray-400 hover:text-white hover:bg-white/[0.06]"
                }`}
              >
                {p}
              </button>
            ))}
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={safePage === totalPages}
              className="w-6 h-6 md:w-7 md:h-7 flex items-center justify-center rounded-md text-gray-400 hover:text-white hover:bg-white/[0.06] disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer transition-colors"
            >
              <i className="ri-arrow-right-s-line text-xs md:text-sm"></i>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}